import React from "react";
import { Button as MuiButton } from "@mui/material";

// --- small, dependency-free color helpers ---
const clamp = (n, min = 0, max = 255) => Math.min(Math.max(n, min), max);
const pad2 = (n) => n.toString(16).padStart(2, "0");

function parseHex(color) {
  if (typeof color !== "string") return null;
  const c = color.trim();
  if (!c.startsWith("#")) return null;
  const hex = c.slice(1);
  if (hex.length === 3) {
    const r = parseInt(hex[0] + hex[0], 16);
    const g = parseInt(hex[1] + hex[1], 16);
    const b = parseInt(hex[2] + hex[2], 16);
    return { r, g, b };
  }
  if (hex.length === 6) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    if ([r, g, b].some((v) => Number.isNaN(v))) return null;
    return { r, g, b };
  }
  return null;
}
function parseRgb(color) {
  if (typeof color !== "string") return null;
  const m = color
    .trim()
    .match(/^rgb\s*\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i);
  if (!m) return null;
  const r = clamp(parseInt(m[1], 10));
  const g = clamp(parseInt(m[2], 10));
  const b = clamp(parseInt(m[3], 10));
  return { r, g, b };
}
function toHex({ r, g, b }) {
  return `#${pad2(clamp(r))}${pad2(clamp(g))}${pad2(clamp(b))}`;
}
function darkenColor(color, amount = 0.18) {
  // amount: 0..1 (18% darker by default)
  if (!color || color === "transparent" || color === "inherit") return null;
  const rgb = parseHex(color) || parseRgb(color);
  if (!rgb) return null;
  const factor = 1 - clamp(amount, 0, 1);
  const r = Math.round(rgb.r * factor);
  const g = Math.round(rgb.g * factor);
  const b = Math.round(rgb.b * factor);
  return toHex({ r, g, b });
}
function getContrastColor(color) {
  const rgb = parseHex(color) || parseRgb(color);
  if (!rgb) return "#FFFFFF";
  // YIQ brightness formula to pick black/white text
  const yiq = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return yiq >= 140 ? "#000000" : "#FFFFFF";
}

const ButtonComponent = ({
  children,
  onClick,
  color = "#242424",
  bgColor = "#F8F8F8",
  borderColor = "#E0E0E0",
  hoverBgColor, // if provided, it wins; otherwise we'll compute a darker shade
  hoverBorderColor,
  hoverColor, // deprecated for now: we keep label color constant on hover/active per UX
  hoverDarkenAmount = 0.1, // slightly darker by default
  maxWidth = undefined,
  paddingY = "16px",
  disabled = false,
  fontSize = 16,
  paddingX = 3,
  maxHeight = undefined,
  ...rest
}) => {
  // Normalize some values
  const resolvedMaxHeight = maxHeight === "full" ? "100%" : maxHeight;

  // Compute dynamic hover colors when not explicitly provided
  const computedHoverBg =
    hoverBgColor || darkenColor(bgColor, hoverDarkenAmount) || "#064E3B";
  // Keep text and border consistent; only background gets slightly darker
  const computedHoverText = color;
  const computedHoverBorder = hoverBorderColor || borderColor;

  return (
    <MuiButton
      disabled={disabled}
      // Allow consumers to pass additional button props (e.g., type, aria-*, etc.)
      {...rest}
      sx={{
        // Apply only if provided to avoid invalid CSS values
        ...(maxWidth ? { maxWidth } : {}),
        textWrap: "nowrap",
        paddingX: paddingX,
        paddingY: `${paddingY} !important`,
        fontSize: fontSize,
        fontWeight: 500,
        letterSpacing: 0.025,
        borderRadius: "12px",
        ...(resolvedMaxHeight ? { maxHeight: resolvedMaxHeight } : {}),
        backgroundColor: `${bgColor} !important`,
        border: `1px solid ${borderColor}`,
        color: color,
        lineHeight: "24px",
        transition: "all 0.3s ease",
        "@media (max-width: 768px)": {
          paddingX: "10px",
          paddingY: `8px !important`,
          fontSize: 14,
        },
        // Increase specificity so MUI default states can't override ours
        "&&:hover": {
          backgroundColor: `${computedHoverBg} !important`,
          border: `1px solid ${computedHoverBorder} !important`,
          color: computedHoverText,
        },
        "&&:active": {
          backgroundColor: `${computedHoverBg} !important`,
          border: `1px solid ${computedHoverBorder} !important`,
          color: computedHoverText,
        },
        "&&:focus, &&:focus-visible": {
          backgroundColor: `${computedHoverBg} !important`,
          border: `1px solid ${computedHoverBorder} !important`,
          color: computedHoverText,
        },
        "&.Mui-disabled": {
          backgroundColor: bgColor,
          border: `1px solid ${borderColor}`,
          color: color,
          cursor: "not-allowed",
          opacity: 0.6,
        },
      }}
      onClick={onClick}
    >
      {children}
    </MuiButton>
  );
};
export default ButtonComponent;
